import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, useFormContext } from 'react-hook-form'
import { Box, IconButton } from '@mui/material'
import { ActivityLogButton, Icon } from 'src/components/atoms'
import CenteringLoading from 'src/components/molecules/CenteringLoading'
import Modal from 'src/components/molecules/Modal/index'
import NextImageLoader from 'src/components/molecules/NextImageLoader'
import { ReportMessage } from 'src/components/molecules/ReportMessages'
import { Option } from 'src/components/molecules/SelectWithGroupOption'
import { Column } from 'src/components/tables/AppTableHead'
import { ENABLE_OCR_HIGHLIGHT_ORG_CODES } from 'src/domain/org'
import { isOneOfRoles } from 'src/domain/role'
import {
  ReceiptImage,
  ReportStatusEnum,
  RoleEnum,
  Store,
  Tenant,
  TenantDetail,
  Notification,
} from 'src/slices/services/api'
import TenantSalesReportDateOptions from '../../components/DateOptions'
import TenantSalesReportInfo from '../../components/Info'
import InputNotificationModal from '../../components/InputNotificationModal'
import { TenantSalesReportMessage } from '../../components/Message'
import { TenantSalesReportNotification } from '../../components/Notification'
import ProgressConfirmBar from '../../components/ProgressConfirmBar'
import SalesDate from '../../components/SalesDate'
import TenantName from '../../components/TenantName'
import TenantSalesReportRegister from '../../components/TenantSalesReportRegister'
import {
  FormNameEnum,
  FormTypesTenantSalesReport,
  Register,
  RegisterWithSum,
} from '../../type'
import { buildExpandedRows } from './buildExpandedRows'
import { calculateSumCell, hasExceededSumCell } from './calculateSumCell'
import styles from './styles.module.scss'

export type TenantSalesReportProps = {
  isLoading?: boolean
  isEnableToFinalizeAndFilterByMisMatchAndSendMessageAndSendNotificationByRoles: boolean
  orgCode: string
  store?: Store
  tenant?: Tenant
  reportStatus?: ReportStatusEnum
  tenantPath: string
  date: string
  selectedDate?: string
  registers: Register[]
  columns: Column[]
  showModal: boolean
  tenantDetail?: TenantDetail
  manageMessage?: string
  notifications?: Notification[]
  disabledConfirmButton?: boolean
  progress: number
  totalCount: number
  currentCount: number
  selectedResentImage?: ReceiptImage
  reportMessages: ReportMessage[]
  role?: RoleEnum
  selectOptions: Option[]
  selectPlaceholder: string
  selectDefaultValue?: string
  selectedTenantCodes?: string[]
  onClickConfirm: () => void
  onClickBack: () => void
  onClickClose: () => void
  onClickSkip: () => void
  onClickModalConfirm: () => void
  onClickModalCancel: () => void
  onClickNotificationModalRegister: (notification: string) => void
  onClickValue: (
    value: string,
    registerIndex: number,
    rowIndex: number,
    childIndex?: number
  ) => void
  onClickHistoryButton?: () => void
}

const TenantSalesReportTemplate: React.StyledFC<TenantSalesReportProps> = ({
  isLoading,
  isEnableToFinalizeAndFilterByMisMatchAndSendMessageAndSendNotificationByRoles,
  orgCode,
  store,
  tenant,
  reportStatus,
  tenantPath,
  date,
  registers,
  columns,
  tenantDetail,
  manageMessage,
  notifications,
  showModal,
  disabledConfirmButton,
  progress,
  totalCount,
  currentCount,
  selectedResentImage,
  reportMessages,
  role,
  selectOptions,
  selectPlaceholder,
  selectDefaultValue,
  selectedDate,
  selectedTenantCodes,
  onClickConfirm,
  onClickBack,
  onClickClose,
  onClickSkip,
  onClickModalConfirm,
  onClickModalCancel,
  onClickNotificationModalRegister,
  onClickValue,
  onClickHistoryButton,
}: TenantSalesReportProps) => {
  const formMethods = useFormContext<FormTypesTenantSalesReport>()
  const rootRef = useRef<HTMLDivElement>(null)
  const [isCurrentImageLoaded, setIsCurrentImageLoaded] =
    useState<boolean>(false)
  const [isNextImageLoaded, setIsNextImageLoaded] = useState<boolean>(false)
  const [nextTenantCode, setNextTenantCode] = useState<string>()
  const [isNextTenantCodeUpdated, setIsNextTenantCodeUpdated] =
    useState<boolean>(false)
  const [isNotificationModalOpen, isSetNotificationModalOpen] =
    useState<boolean>(false)
  const [showHighlights, setShowHighlights] = useState<boolean>(true)
  const [expandedRows, setExpandedRows] = useState<Map<number, Set<number>>>(
    () => buildExpandedRows(registers, orgCode)
  )

  useEffect(() => {
    setShowHighlights(true)
    setExpandedRows(buildExpandedRows(registers, orgCode))
  }, [registers, orgCode])

  const handleChangeHighlightEnabled = useCallback((enabled: boolean) => {
    setShowHighlights(enabled)
  }, [])

  const handleToggleRow = useCallback(
    (registerIndex: number, rowIndex: number) => {
      setExpandedRows((prev) => {
        const next = new Map(prev)
        const set = new Set(next.get(registerIndex) || [])
        if (set.has(rowIndex)) {
          set.delete(rowIndex)
        } else {
          set.add(rowIndex)
        }
        next.set(registerIndex, set)
        return next
      })
    },
    []
  )

  const visibleHighlightsPerRegister = useMemo(() => {
    return registers.map((register, ri) => {
      const expanded = expandedRows.get(ri)
      if (!expanded || expanded.size === 0) return []
      // child.key と h.ocrFormatId は共に ocrFormat.id から生成されるため一致する
      const activeOcrFormatIds = new Set<string>()
      expanded.forEach((rowIdx) => {
        const row = register.rows[rowIdx]
        if (row?.highlightColorIndex !== undefined) {
          row.childRows.forEach((child) => activeOcrFormatIds.add(child.key))
        }
      })
      return register.highlights.filter((h) =>
        activeOcrFormatIds.has(h.ocrFormatId)
      )
    })
  }, [expandedRows, registers])
  // NOTE: 画像の事前ロードを行うNextImageLoaderを呼びだすための条件 tenantの画像のロードが完了していて一つ次のtenantの画像は未ロードの場合、trueになる
  const isReadyToLoadNextImage =
    isCurrentImageLoaded && !isNextImageLoaded && isNextTenantCodeUpdated
  // NOTE: スキップを押した時にテーブルの状態を新しいテナントのものに移行するためのフラグ
  // スキップが押された時に別テナントに遷移中(=true)になり、新しいテナントの情報が取得されuseEffectが走るタイミングでfalse(遷移完了)になる
  const [underTransition, setUnderTransition] = useState<boolean>(true)
  const scrollTop = () => {
    if (rootRef.current?.scroll) {
      rootRef.current.scroll(0, 0)
    }
  }

  useEffect(() => {
    // NOTE: 一つ次のtenantSalesReportを取得
    if (currentCount && selectedTenantCodes) {
      const newNextTenantCode = selectedTenantCodes[currentCount]
      setNextTenantCode(newNextTenantCode)
      setIsNextTenantCodeUpdated(true)
    }
  }, [currentCount, selectedTenantCodes])

  const inputCellWatcher = formMethods.watch(FormNameEnum.registers)

  const [registerWithSums, setRegisterWithSums] = useState<RegisterWithSum[]>(
    calculateSumCell(registers, formMethods.getValues())
  )

  useEffect(() => {
    setUnderTransition(false)
    const calculatedValue = calculateSumCell(registers, formMethods.getValues())
    setRegisterWithSums(calculatedValue)
  }, [inputCellWatcher, registers, formMethods, setRegisterWithSums])

  const handleClickFinalize = () => {
    scrollTop()
    onClickConfirm()
    setIsCurrentImageLoaded(false)
    setIsNextImageLoaded(false)
    setIsNextTenantCodeUpdated(false)
  }

  const handleClickBack = () => {
    scrollTop()
    onClickBack()
    setIsNextImageLoaded(true)
    setIsNextTenantCodeUpdated(true)
  }

  const handleClickSkip = () => {
    setUnderTransition(true)
    scrollTop()
    onClickSkip()
    setIsCurrentImageLoaded(false)
    setIsNextImageLoaded(false)
    setIsNextTenantCodeUpdated(false)
  }

  const handleCurrentImageLoaded = () => {
    setIsCurrentImageLoaded(true)
  }

  const handleNextImageLoaded = () => {
    setIsNextImageLoaded(true)
    setIsNextTenantCodeUpdated(false)
  }

  const handleClickSendNotification = (inputNotification: string): void => {
    onClickNotificationModalRegister(inputNotification)
    isSetNotificationModalOpen(false)
  }

  return (
    <>
      <FormProvider {...formMethods}>
        <form>
          <Box className={styles.root} ref={rootRef}>
            <Box className={styles.header}>
              <Box className={styles.headerMainColumn}>
                <TenantSalesReportDateOptions
                  className={styles.headerMainLeft}
                  selectOptions={selectOptions}
                  selectPlaceholder={selectPlaceholder}
                  defaultValue={selectDefaultValue}
                />
                <Box className={styles.headerMainRight}>
                  <TenantName tenant={tenant} reportStatus={reportStatus} />
                  {onClickHistoryButton &&
                    isOneOfRoles([RoleEnum.Admin], role) && (
                      <ActivityLogButton onClick={onClickHistoryButton} />
                    )}
                </Box>
              </Box>
              <Box className={styles.headerRightSideBar}>
                <SalesDate date={date} />
                <Box className={styles.closeButtonWrapper}>
                  <IconButton
                    className={styles.closeButton}
                    onClick={onClickClose}
                    size="large"
                  >
                    <Icon icon="close" />
                  </IconButton>
                </Box>
              </Box>
            </Box>
            <Box className={styles.body}>
              <Box className={styles.bodyMainColumn}>
                {/* NOTE: 別テナントにスキップした時に一つ前のchildRowのデータが引き継がれてしまう */}
                {/* スキップでテナントが変わるたびにHTMLからテーブルを消すことでテーブルの状態をリセットする */}
                {isLoading || underTransition ? (
                  <CenteringLoading />
                ) : (
                  <>
                    {registerWithSums.map((register, registerIndex) => (
                      <TenantSalesReportRegister
                        className={styles.mainContentList}
                        key={register.key}
                        columns={columns}
                        rows={register.rows}
                        orgCode={orgCode}
                        role={role}
                        concatOriginal={
                          selectedResentImage?.concatOriginal ||
                          register.concatOriginal
                        }
                        separatedOriginal={
                          selectedResentImage?.separatedOriginal ||
                          register.separatedOriginal
                        }
                        registerIndex={registerIndex}
                        resized={
                          selectedResentImage?.resized || register.resized
                        }
                        isPdf={
                          selectedResentImage
                            ? selectedResentImage.isPdf
                            : register.isPdf
                        }
                        onClickValue={(value, rowIndex, childIndex) =>
                          onClickValue(
                            value,
                            registerIndex,
                            rowIndex,
                            childIndex
                          )
                        }
                        isFirst={registerIndex === 0}
                        onLoaded={handleCurrentImageLoaded}
                        tenantRegister={register.tenantRegister}
                        highlights={
                          !ENABLE_OCR_HIGHLIGHT_ORG_CODES.includes(orgCode) ||
                          selectedResentImage
                            ? []
                            : visibleHighlightsPerRegister[registerIndex]
                        }
                        showHighlights={
                          showHighlights &&
                          ENABLE_OCR_HIGHLIGHT_ORG_CODES.includes(orgCode)
                        }
                        showHighlightSelector={Boolean(
                          ENABLE_OCR_HIGHLIGHT_ORG_CODES.includes(orgCode) &&
                            register.highlights &&
                            register.highlights.length > 0
                        )}
                        onChangeHighlightEnabled={handleChangeHighlightEnabled}
                        expandedRows={expandedRows.get(registerIndex)}
                        onToggleRow={(rowIndex) =>
                          handleToggleRow(registerIndex, rowIndex)
                        }
                      />
                    ))}
                  </>
                )}
              </Box>
              <Box className={styles.bodyRightSideBar}>
                <Box className={styles.stickyBar}>
                  <TenantSalesReportInfo
                    tenant={tenant}
                    tenantDetail={tenantDetail}
                    store={store}
                    tenantPath={tenantPath}
                  />
                  <Box className={styles.messagesAndNotificationContainer}>
                    <TenantSalesReportMessage
                      reportMessages={reportMessages}
                      tenantMessageBody={tenantDetail?.note || ''}
                      manageMessage={manageMessage}
                      tenantPath={tenantPath}
                      isEnableToFinalizeAndFilterByMisMatchAndSendMessageAndSendNotificationByRoles={
                        isEnableToFinalizeAndFilterByMisMatchAndSendMessageAndSendNotificationByRoles
                      }
                    />
                    <TenantSalesReportNotification
                      notifications={notifications}
                      orgCode={orgCode}
                      isEnableToFinalizeAndFilterByMisMatchAndSendMessageAndSendNotificationByRoles={
                        isEnableToFinalizeAndFilterByMisMatchAndSendMessageAndSendNotificationByRoles
                      }
                      onClickNotificationButton={() =>
                        isSetNotificationModalOpen(true)
                      }
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box>
              <ProgressConfirmBar
                className={styles.footerBar}
                orgCode={orgCode}
                progress={progress}
                totalCount={totalCount}
                hasExceededSumCell={hasExceededSumCell(
                  registerWithSums,
                  orgCode
                )}
                currentCount={currentCount}
                disabledConfirmButton={disabledConfirmButton}
                isEnableToFinalizeAndFilterByMisMatchAndSendMessageAndSendNotificationByRoles={
                  isEnableToFinalizeAndFilterByMisMatchAndSendMessageAndSendNotificationByRoles
                }
                onClickSkip={handleClickSkip}
                onClickConfirm={handleClickFinalize}
                onClickBack={handleClickBack}
              />
            </Box>
          </Box>
          <Modal
            open={showModal}
            title={`今一覧に戻ると、テナントの選択状態がリセットされます。\nよろしいですか？`}
            agreeButtonTitle="一覧に戻る"
            disagreeButtonTitle="キャンセル"
            onClickAgree={onClickModalConfirm}
            onClickDisagree={onClickModalCancel}
            onClose={onClickModalCancel}
          />
          {/* NOTE: tenantの画像のロードが完了していて一つ次のtenantの画像は未ロードの場合、isReadyToLoadNextImageがtrueになり、NextImageLoaderが呼び出されて事前ロードが実行される */}
          {isReadyToLoadNextImage &&
            selectedDate &&
            nextTenantCode &&
            store && (
              <NextImageLoader
                orgCode={orgCode}
                storeCode={store?.code}
                selectedDate={selectedDate}
                nextTenantCode={nextTenantCode}
                onLoadComplete={handleNextImageLoaded}
              />
            )}
        </form>
      </FormProvider>
      {/* NOTE: InputNotificationModalを明示的に削除して、FormをResetする */}
      {isLoading || underTransition ? null : (
        <InputNotificationModal
          open={isNotificationModalOpen}
          onClickClose={() => {
            isSetNotificationModalOpen(false)
          }}
          onClickSend={handleClickSendNotification}
        />
      )}
    </>
  )
}

TenantSalesReportTemplate.displayName = 'TenantSalesReportTemplate'
export default TenantSalesReportTemplate
